import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  Bell,
  History,
  Moon,
  PanelRight,
  Search,
  Sparkles,
  Sun,
} from "lucide-react";

import { TWIDO_APP_NAME, TWIDO_LOGO_SRC } from "@/branding";

import { cn } from "@/lib/utils";
import { useUserOptional } from "@/auth/UserContext";
import { useAppBootstrap } from "@/context/AppBootstrapContext";
import { CLIENT_PORTAL_WORKSPACE_SUBTITLE } from "@/lib/clientPortalDisplay";
import { useRightRail } from "@/context/RightRailContext";
import { useTheme } from "@/context/ThemeContext";
import { useKaiChatOptional } from "@/features/kai/KaiChatContext";
import { useOpenKai } from "@/hooks/useOpenKai";
import { Button } from "@/components/ui/button";
import { isMobileSheetViewport } from "@/lib/viewport";
import { useTopBarSubtitleOverride } from "@/context/TopBarSubtitleContext";
import { getTopBarPageContext } from "./topBarContext";
import { OrganizationPill } from "./OrganizationPill";
import { UserAccountMenu } from "./UserAccountMenu";
import { useFeedOptional } from "@/context/FeedContext";
import { useGlobalSearchUIOptional } from "@/context/GlobalSearchContext";

const BUBBLE_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** ISO week from the local calendar date, e.g. "W35-2026". */
function isoWeekLabel(date: Date): string {
  const thursday = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  const year = thursday.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil(
    ((thursday.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7,
  );
  return `W${String(week).padStart(2, "0")}-${year}`;
}

function FlipClock({ now, className }: { now: Date; className?: string }) {
  const time = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const week = isoWeekLabel(now);
  const date = now.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const flipped = `${week} · ${date}`;
  const sizer = flipped.length >= time.length ? flipped : time;

  return (
    <span
      className={cn(
        "group/clock relative inline-flex h-7 cursor-default select-none items-center px-2 text-sm tabular-nums text-muted-foreground",
        className,
      )}
      aria-label={`${time}, ${flipped}`}
    >
      <span className="invisible select-none whitespace-nowrap" aria-hidden>
        {sizer}
      </span>
      <span className="absolute inset-x-2 inset-y-0 [perspective:90px]">
        <span
          className={cn(
            "relative block h-full w-full [transform-style:preserve-3d]",
            "transition-transform duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
            "group-hover/clock:[transform:rotateX(180deg)]",
          )}
        >
          <span className="absolute inset-0 flex items-center justify-center whitespace-nowrap [backface-visibility:hidden]">
            {time}
          </span>
          <span className="absolute inset-0 flex items-center justify-center whitespace-nowrap [backface-visibility:hidden] [transform:rotateX(180deg)]">
            {flipped}
          </span>
        </span>
      </span>
    </span>
  );
}

/** Static bubble — no width animation needed. */
function NavBubble({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "shell-chrome glass-pill clay glass-no-hover topbar-pill flex min-h-14 w-fit items-center gap-2 p-1.5 md:min-h-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Actions pill — measures inner content then CSS-transitions explicit pixel width.
 * Requires the TopBar to stay mounted across route changes (see AppShellLayout).
 */
function AnimatedNavBubble({
  className,
  layoutKey,
  children,
}: {
  className?: string;
  layoutKey: string;
  children: ReactNode;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [canTransition, setCanTransition] = useState(false);
  const { phase } = useAppBootstrap();

  /* Defer pill width transitions until boot finishes — avoids glass reflow pop. */
  useEffect(() => {
    if (phase !== "ready") {
      setCanTransition(false);
      return;
    }
    const frame = requestAnimationFrame(() => setCanTransition(true));
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return;

    const target = node.offsetWidth;

    if (widthRef.current === null) {
      widthRef.current = target;
      setWidth(target);
      return;
    }

    if (target === widthRef.current) return;

    /* Double rAF: paint once with the stale outer width, then apply the new
       width on the next frame so transition-[width] actually runs. */
    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        widthRef.current = target;
        setWidth(target);
      });
    });

    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [layoutKey]);

  return (
    <div
      className={cn(
        // glass-pill must live on the shell-chrome element itself: a nested
        // glass surface inside shell-chrome's clip-path loses its
        // backdrop-filter (clip-path forms a backdrop root), rendering flat.
        "shell-chrome glass-pill clay glass-no-hover topbar-pill min-h-14 overflow-hidden rounded-full md:min-h-0",
        className,
      )}
      style={{
        width: width ?? undefined,
        transition:
          canTransition && width != null
            ? `width 350ms ${BUBBLE_EASE}`
            : undefined,
      }}
    >
      <div
        ref={innerRef}
        className="topbar-pill-inner flex min-h-14 w-max items-center gap-2 p-1.5 md:min-h-0"
      >
        {children}
      </div>
    </div>
  );
}

export function TopBar() {
  const { pathname } = useLocation();
  const onKaiPage = pathname === "/";
  const onFeedPage = pathname.startsWith("/feed");
  const userCtx = useUserOptional();
  const pageContext = getTopBarPageContext(pathname);
  const subtitleOverride = useTopBarSubtitleOverride();
  const baseSubtitle = subtitleOverride ?? pageContext.subtitle;
  const subtitle =
    userCtx?.isClient && baseSubtitle === "Your workspace"
      ? CLIENT_PORTAL_WORKSPACE_SUBTITLE
      : baseSubtitle;
  const { theme, toggleTheme } = useTheme();
  const kai = useKaiChatOptional();
  const openKai = useOpenKai();
  const {
    kaiPanelActive,
    toggleNotificationsPanel,
    notificationsPanelActive,
    mobileRailOpen,
    mobileRailMode,
    openMobileRail,
    setMobileRailOpen,
  } = useRightRail();
  const now = useClock();

  const showKaiAction = pageContext.actions.some((action) => action.id === "kai");

  const handleKaiClick = () => {
    kai?.closeHistory();
    openKai();
  };

  const handleNotificationsClick = () => {
    kai?.closeHistory();

    if (isMobileSheetViewport()) {
      const notificationsSheetOpen =
        mobileRailOpen && mobileRailMode === "notifications";

      if (notificationsPanelActive && notificationsSheetOpen) {
        toggleNotificationsPanel();
        setMobileRailOpen(false);
        return;
      }

      openMobileRail("notifications");
      return;
    }

    toggleNotificationsPanel();
  };

  const handleWorkspaceClick = () => {
    kai?.closeHistory();
    openMobileRail("workspace");
  };

  const handleHistoryClick = () => {
    setMobileRailOpen(false);
    kai?.toggleHistory();
  };

  const unreadCount = useFeedOptional()?.unreadCount ?? 0;
  const historyOpen = kai?.historyOpen ?? false;
  const globalSearch = useGlobalSearchUIOptional();

  return (
    <header className="flex w-full justify-center">
      <div className="flex items-center gap-2 md:gap-2.5">
        <AnimatedNavBubble
          className="shrink-0"
          layoutKey={`logo-${subtitle}`}
        >
          <img
            src={TWIDO_LOGO_SRC}
            alt={TWIDO_APP_NAME}
            className="size-11 shrink-0 rounded-full md:size-9"
          />
          <div className="pr-3 leading-tight">
            <p className="text-sm font-semibold">{TWIDO_APP_NAME}</p>
            <p className="text-[11px] text-muted-foreground">
              {subtitle}
            </p>
          </div>
        </AnimatedNavBubble>

        <NavBubble className="hidden shrink-0 md:flex">
          <UserAccountMenu variant="desktop" />

          <span className="hidden h-7 w-px bg-border/60 lg:block" />

          <FlipClock now={now} className="hidden lg:inline-flex" />
        </NavBubble>

        <OrganizationPill className="hidden shrink-0 md:flex" />

        <AnimatedNavBubble
          className="shrink-0"
          layoutKey={`${pageContext.id}-${onFeedPage ? "nobell" : "bell"}`}
        >
          <Button
            variant="shell"
            size="touchIcon"
            aria-label="Search"
            aria-keyshortcuts="Meta+K Control+K"
            onClick={() => globalSearch?.openSearch()}
          >
            <Search className="size-4" />
          </Button>

          <Button
            variant="shell"
            size="icon"
            className="hidden md:inline-flex"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={toggleTheme}
          >
            {theme === "dark" ? (
              <Sun className="size-4" />
            ) : (
              <Moon className="size-4" />
            )}
          </Button>

          {!onFeedPage && (
            <Button
              variant="shell"
              size="touchIcon"
              className="relative"
              aria-label={
                notificationsPanelActive ? "Hide notifications" : "Notifications"
              }
              aria-pressed={notificationsPanelActive}
              onClick={handleNotificationsClick}
            >
              <Bell className="size-4" />
              {unreadCount > 0 && !notificationsPanelActive && (
                <span
                  className={cn(
                    "absolute right-2 top-2 size-2 rounded-full bg-sky-400",
                    "shadow-[0_0_8px_2px_rgba(90,205,255,0.6)]",
                  )}
                />
              )}
            </Button>
          )}

          {showKaiAction && (
            <Button
              variant="shell"
              size="icon"
              className={cn(
                "hidden motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200 md:inline-flex",
                !kaiPanelActive && "text-primary",
              )}
              aria-label={kaiPanelActive ? "Close Kai" : "Ask Kai"}
              aria-pressed={kaiPanelActive}
              onClick={handleKaiClick}
            >
              <Sparkles className="size-4" />
            </Button>
          )}

          {onKaiPage ? (
            <Button
              variant="shell"
              size="touchIcon"
              className="lg:hidden"
              aria-label={historyOpen ? "Close chat history" : "Chat history"}
              aria-pressed={historyOpen}
              onClick={handleHistoryClick}
            >
              <History className="size-4" />
            </Button>
          ) : (
            <Button
              variant="shell"
              size="touchIcon"
              className="lg:hidden"
              aria-label="Open workspace"
              onClick={handleWorkspaceClick}
            >
              <PanelRight className="size-4" />
            </Button>
          )}

          <UserAccountMenu variant="mobile" className="md:hidden" />
        </AnimatedNavBubble>
      </div>
    </header>
  );
}
